import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {
  Box,
  Chip,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { parseISO } from "date-fns";

import MDBox from "components/MDBox";
import { formatCurrency } from "helpers/utils";
import { formatDate } from "helpers/formatDate";
import type { ILogCartaoEntry, LogCobrancaStatus } from "interfaces/logsCartao";
import {
  CATEGORIA_LABELS,
  STATUS_LABELS,
} from "interfaces/logsCartao";
import { LOGS_COBRANCAS_ACCENT, modalHeaderSx, modalPaperSx } from "../logs-cobrancas.theme";

function formatLogTimestamp(iso: string): string {
  if (!iso) return "—";
  try {
    return formatDate(parseISO(iso), "dd/MM/yyyy HH:mm:ss");
  } catch {
    return iso;
  }
}

function formatLogCartaoValor(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const reais = Number.isInteger(value) ? value / 100 : value;
  return formatCurrency(reais);
}

function StatusPill({ status }: { status: LogCobrancaStatus }) {
  const label = STATUS_LABELS[status] ?? status;
  const styles: Record<
    LogCobrancaStatus,
    { bg: string; border: string; color: string }
  > = {
    SUCESSO: {
      bg: "rgba(46, 125, 50, 0.1)",
      border: "#2e7d32",
      color: "#2e7d32",
    },
    ERRO: {
      bg: "rgba(211, 47, 47, 0.1)",
      border: "#d32f2f",
      color: "#d32f2f",
    },
    AVISO: {
      bg: "rgba(237, 108, 2, 0.1)",
      border: "#ed6c02",
      color: "#ed6c02",
    },
    INFO: {
      bg: "rgba(249, 96, 48, 0.1)",
      border: LOGS_COBRANCAS_ACCENT,
      color: LOGS_COBRANCAS_ACCENT,
    },
  };
  const s = styles[status] ?? styles.INFO;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.25,
        py: 0.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: s.border,
        bgcolor: s.bg,
        color: s.color,
        fontSize: "0.8125rem",
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

function formatLogText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

interface Props {
  open: boolean;
  log: ILogCartaoEntry | null;
  onClose: () => void;
}

export default function LogCobrancaDetailDialog({ open, log, onClose }: Props) {
  if (!log) return null;

  const categoriaLabel = CATEGORIA_LABELS[log.categoria] ?? log.categoria;
  const messageText = formatLogText(log.message);
  const detailText = log.detail ? formatLogText(log.detail) : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 36, opacity: 0.9, flexShrink: 0 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                Detalhe do log
              </Typography>
              <Typography variant="h6" fontWeight="bold" noWrap>
                {categoriaLabel}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" mt={0.75} flexWrap="wrap">
                <StatusPill status={log.status} />
                <Chip
                  size="small"
                  label={log.level.toUpperCase()}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.65rem",
                  }}
                />
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <MDBox sx={{ p: 3, overflowY: "auto" }}>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6}>
            <InfoItem label="Data/Hora" value={formatLogTimestamp(log.timestamp)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoItem label="Referência" value={log.numeroInscricao ?? "—"} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <InfoItem label="Valor" value={formatLogCartaoValor(log.totalInscricao)} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <InfoItem
              label="Parcelas"
              value={log.installments != null ? String(log.installments) : "—"}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InfoItem
              label="Forma de pagamento"
              value={log.formaPagamento ?? "—"}
            />
          </Grid>
          {log.customer ? (
            <>
              <Grid item xs={12} sm={6}>
                <InfoItem label="Cliente" value={log.customer.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem label="CPF" value={log.customer.cpf} />
              </Grid>
              {log.customer.email ? (
                <Grid item xs={12} sm={6}>
                  <InfoItem label="E-mail" value={log.customer.email} />
                </Grid>
              ) : null}
              {log.customer.phone ? (
                <Grid item xs={12} sm={6}>
                  <InfoItem label="Telefone" value={log.customer.phone} />
                </Grid>
              ) : null}
            </>
          ) : null}
          {log.erroCodigo ? (
            <Grid item xs={12} sm={4}>
              <InfoItem label="Código do erro" value={log.erroCodigo} />
            </Grid>
          ) : null}
        </Grid>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} mb={1}>
          Mensagem
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            mb: detailText ? 2 : 0,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.300",
            bgcolor: log.status === "ERRO" ? "rgba(211, 47, 47, 0.04)" : "grey.50",
            fontSize: "0.8125rem",
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "inherit",
          }}
        >
          {messageText || "—"}
        </Box>

        {detailText ? (
          <>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Detalhes / stack trace
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.300",
                bgcolor: "grey.900",
                color: "grey.100",
                fontSize: "0.75rem",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
              }}
            >
              {detailText}
            </Box>
          </>
        ) : null}
      </MDBox>
    </Dialog>
  );
}
